import type { SiteAnnouncementItem } from '@app/_proto/Protos/site_announcements';
import { ReactComponent as Pin } from '@app/assets/pin.svg';
import LazyAvatar from '@app/components/lazy-avatar';
import useInViewForPosthog from '@app/domains/common/hooks/useInViewForPosthog';
import { getSiteAnnouncementUrl } from '@app/domains/common/utils/path';
import { HomeEvents } from '@app/domains/home/analytics/posthog/events';
import { timestampToUnix } from '@app/libs/utils';
import { useTimeAgo } from '@app/utils/time';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

type Props = {
    siteAnnouncement: SiteAnnouncementItem;
};

export default function HomeSiteAnnouncement({ siteAnnouncement }: Props) {
    const { announcer, slug, title, timePosted, isSticky } = siteAnnouncement;
    const { avatarUrl, userName } = announcer ?? {};
    const isRecent = useMemo(() => {
        const now = dayjs();
        const announcementPostedTime = dayjs.unix(timestampToUnix(timePosted) as number);
        return !announcementPostedTime.isBefore(now.subtract(7, 'day'));
    }, [timePosted]);
    const timeAgo = useTimeAgo(timestampToUnix(timePosted), {
        refreshInterval: 0,
    });

    const { ref } = useInViewForPosthog({
        event: HomeEvents.ViewCollectionItem,
        triggerOnce: true,
    });

    return (
        <section className="h-72 w-full rounded-12 bg-gray-container-base md:h-87" ref={ref}>
            <Link
                className="flex h-full w-full space-x-12 p-12"
                to={getSiteAnnouncementUrl(slug ?? '')}
                data-posthog-click-event={HomeEvents.ClickCollectionItem}
            >
                <div className="relative">
                    <div className="absolute z-[1] inline-block h-36 w-36 rounded-6 shadow-ww-site-announcement-avatar-inner md:h-48 md:w-48"></div>
                    <LazyAvatar
                        className="h-36 w-36 rounded-6 border-none md:h-48 md:w-48"
                        src={avatarUrl?.value}
                        alt={userName}
                        imageRenderSize={48}
                    />

                    {isSticky && (
                        <span className="absolute bottom-3 -right-4 z-[2] inline-flex h-20 w-20 items-center justify-center rounded-[50%] bg-blue-600 pl-1 md:-right-3 md:bottom-10">
                            <Pin className="h-12 w-12 text-white" />
                        </span>
                    )}
                </div>
                <div className="font-set-sb15 md:font-set-sb18">
                    <p className="text-gray-t1 line-clamp-2">
                        {!isSticky && isRecent && (
                            <span className="mr-8 inline-block h-8 w-8 rounded-[50%] bg-blue-600"></span>
                        )}
                        {title}
                    </p>
                    <span className="font-set-r13 text-gray-desc md:font-set-r14">{timeAgo}</span>
                </div>
            </Link>
        </section>
    );
}
